<template>
    <div id="room-toolbar"
         ref="toolbar"
         :class="[full_viewport ? 'position-full-viewport' : 'position-in-event', 'tw-absolute tw-w-full tw-flex tw-justify-center']">
        <div class="tw-flex tw-justify-between tw-items-center tw-bg-black tw-rounded-full tw-px-2 tw-py-2 toolbar">
            <!-- Left button group -->
            <div class="tw-flex tw-gap-1">
                <!-- MIC -->
                <toolbar-button
                    name="mic"
                    v-if="livekit_room.localParticipant.permissions.canPublish"
                    :label="[$t('live.unmute_mic'), $t('live.mute_mic')]"
                    :icon_class="['fa-microphone-slash', 'fa-microphone']"
                    :is_muted="live_room.local_microphone_is_muted"
                    :has_options="audio_devices.length > 1"
                    :on_click="muteMicrophone"
                    :on_chevron_click="showAudioDevicesList"
                    :options_is_open="show_audio_devices_list"
                    :active="show_audio_devices_list"
                    :hide_label="sidepanel_is_expanded && width < 730"
                >
                    <toolbar-card
                        name="mic"
                        v-show="show_audio_devices_list"
                        :title="$t('live.select_microphone')"
                        :is_showing="show_audio_devices_list"
                        @close="!disable_click_outside ? show_audio_devices_list = false : null"
                        class="position-left-card"
                    >
                        <template v-slot:main>
                            <toolbar-card-list-item
                                :key="device.deviceId"
                                v-for="device in audio_devices"
                                @click.native="switchAudioDevice(device)"
                                :icon_class="['fa-microphone', 'fa-microphone-slash']"
                                :icon_style="'transform: translateY(2px);'"
                                :title="device.label"
                                :active="device.deviceId === active_audio_device.deviceId"
                            />
                        </template>
                    </toolbar-card>
                </toolbar-button>

                <!-- VIDEO -->
                <toolbar-button
                    name="video"
                    v-if="livekit_room.localParticipant.permissions.canPublish"
                    :label="[$t('live.start_video'), $t('live.stop_video')]"
                    :icon_class="['fa-video-slash', 'fa-video']"
                    :is_muted="live_room.local_camera_is_muted"
                    :on_click="muteCamera"
                    :has_options="camera_devices.length > 1"
                    :on_chevron_click="showCameraDevicesList"
                    :options_is_open="show_camera_devices_list"
                    :active="show_camera_devices_list"
                    :hide_label="sidepanel_is_expanded && width < 730"
                >
                    <toolbar-card
                        name="video"
                        v-show="show_camera_devices_list"
                        :title="$t('live.select_camera')"
                        :is_showing="show_camera_devices_list"
                        @close="!disable_click_outside ? show_camera_devices_list = false : null"
                        class="position-left-card"
                    >
                        <template v-slot:main>
                            <toolbar-card-list-item
                                :key="device.deviceId"
                                v-for="device in camera_devices"
                                @click.native="switchCameraDevice(device)"
                                :icon_class="['fa-video', 'fa-video-slash']"
                                :icon_style="'transform: translateY(2px);'"
                                :title="device.label"
                                :active="device.deviceId === active_camera_device.deviceId"
                            />
                        </template>
                    </toolbar-card>
                </toolbar-button>

                <!-- SCREEN -->
                <toolbar-button
                    name="screen"
                    v-if="livekit_room.localParticipant.permissions.canPublish && live_room_participant.room_admin"
                    :label="[$t('live.share_screen'), $t('live.stop_screensharing')]"
                    :icon_class="['fa-desktop', 'fa-stop']"
                    :is_muted="live_room.local_screen_is_muted"
                    :has_options="!live_room.local_screen_is_muted"
                    :on_click="muteScreen"
                    :on_chevron_click="chooseScreenTrack"
                    :options_is_open="screen_options_is_open"
                    :hide_label="sidepanel_is_expanded && width < 730"
                />

                <!-- PARTICIPANTS -->
                <toolbar-button
                    name="participants"
                    v-if="live_room.status === 'STARTED' && live_room_participant.room_admin"
                    :label="$t('live.participants')"
                    icon_class="fa-users"
                    :on_click="showParticipantsList"
                    :active="show_participant_list"
                    :hide_label="sidepanel_is_expanded && width < 730"
                ></toolbar-button>
                <toolbar-card
                    name="participants"
                    v-show="show_participant_list"
                    :title="$t('live.make_cohost')"
                    :is_showing="show_participant_list"
                    @close="show_participant_list = false"
                    class="participant-card position-participants-card"
                    variant="participant"
                >
                    <template  v-slot:input>
                        <div class="tw-relative" style="margin-bottom: 16px;">
                            <i class="fas fa-search participants-search-icon"></i>
                            <input
                                id="room-participants-search"
                                type="text"
                                v-model.trim="search_query"
                                :placeholder="$t('search.placeholder')"
                                class="participants-search-input"
                            />
                        </div>
                    </template>
                    <template v-slot:main>
                        <template v-if="sorted_participants.length > 0 && livekit_room.participants.size > 0">
                            <toolbar-card-participant-item
                                v-for="participant in sorted_participants.slice(0, 4)"
                                v-if="participant.permissions.canPublish || invited_participant_ids.includes(Number(participant.identity))"
                                :key="participant.sid"
                                :participant="participant"
                                :has_pending_invite="invited_participant_ids.includes(Number(participant.identity))"
                                :reached_max_hosts="false"
                                :room_admin="live_room_participant.room_admin"
                                @iconClicked="handleToolbarCardItemClicked(participant)"
                            />
                            <!-- TODO: hier gebleven: https://github.com/huddlebv/huddle/pull/2205/files                            -->
                            <span v-if="sorted_participants[0].permissions.canPublish" class="tw-width-full tw-height-1 tw-border-b tw-border-gray-100" />
                            <toolbar-card-participant-item
                                v-for="participant in sorted_participants"
                                v-if="!participant.permissions.canPublish && !invited_participant_ids.includes(Number(participant.identity))"
                                :key="participant.sid"
                                :participant="participant"
                                :has_pending_invite="false"
                                :reached_max_hosts="publishers_length >= live_room.max_hosts"
                                :room_admin="live_room_participant.room_admin"
                                @iconClicked="toggleCoHost(participant)"
                            />
                        </template>
                        <div v-else-if="livekit_room.participants.size === 0" class="tw-flex tw-flex-col tw-items-center" style="min-height: 4rem;">
                            <img class="tw-w-7 tw-h-7 tw-mb-3 tw-rounded-full" src="https://www.gravatar.com/avatar/865ccc56009fe05c9a393936ae87a19c?s=80&d=mm&r=g"
                                 alt="avatar of user"/>
                            <span style="font-size: 12px; color: #37474F;">{{ $t('live.no_participants')}}</span>
                        </div>
                    </template>
                </toolbar-card>

                <!-- CHAT -->
                <toolbar-button
                    name="chat"
                    v-if="live_room.status === 'STARTED'"
                    :label="$t('live.chat')"
                    icon_class="fa-comments"
                    :on_click="expandSidePanel"
                    :active="sidepanel_is_expanded"
                    :hide_label="sidepanel_is_expanded && width < 730"
                />
            </div>

            <!-- Right button group -->
            <div class="tw-flex tw-items-center tw-h-full">
                <volume-slider
                    :live_room="live_room"
                    v-if="live_room"
                />

                <toolbar-card
                    name="end_dialog"
                    v-show="show_cant_play_audio_dialog"
                    :title="$t('live.enable_sound')"
                    :is_showing="show_end_dialog"
                    @close="show_cant_play_audio_dialog = false"
                    class="position-leave-card"
                >
                    <template v-slot:main>
                        <div class="general-button btn-v2 slim" @click="playAudio">
                            <i class="fa fa-volume"></i>
                            {{ $t('general.enable') }}
                        </div>
                    </template>
                </toolbar-card>

                <clickable-icon
                    v-if="fullscreen_enabled && live_room_participant" @click.native="toggleFullscreen"
                    class="tw-ml-2 clickable-icon-override tw-transition-opacity"
                    :class="{'tw-mr-3': live_room_participant.room_admin}"
                >
                    <i :class="[{ 'fa-compress': fullscreen , 'fa-expand': !fullscreen}, 'far fa-lg tw-text-white tw-translate-y-px']"/>
                </clickable-icon>

                <template
                    v-if="(live_room.status === 'STAGING' || live_room.status === 'PLANNED')  && live_room_participant.room_admin">
                    <div class="general-button general-button-override bg-color-blue tw-flex tw-justify-center tw-items-center tw-h-full" @click="goLive">
                        {{ $t('live.go_live') }}
                    </div>
                </template>

                <div class="tw-relative tw-h-full">
                    <div v-if="live_room.status === 'STARTED' && live_room_participant.room_admin"
                         id="room-controls-button-end_dialog"
                         class="general-button general-button-override bg-color-red tw-relative tw-flex tw-justify-center tw-items-center tw-h-full"
                         @click="showPrompt">
                        {{ $t('live.end') }}
                    </div>

                    <toolbar-card
                        name="end_dialog"
                        v-show="show_end_dialog"
                        :title="$t('live.end_session')"
                        :is_showing="show_end_dialog"
                        @close="show_end_dialog = false"
                        class="position-leave-card"
                    >
                        <template v-slot:main>
                            <div class="general-button bg-color-red slim" @click="endLive">
                                {{ $t('live.end_session_accept') }}
                            </div>
                            <div class="general-button btn-v2 transparent slim" @click="show_end_dialog = false">
                                {{ $t('live.end_session_decline') }}
                            </div>
                        </template>
                    </toolbar-card>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ToolbarButton from './ToolbarButton.vue'
import {ParticipantEvent, Room, RoomEvent, Track} from "livekit-client";
import ToolbarCardListItem from './ToolbarCardListItem.vue';
import ToolbarCard from './ToolbarCard.vue';
import {getParticipantToken, updateRoomStatus} from "../../mixins/v2/LiveHelpers";
import VolumeSlider from "./VolumeSlider.vue";
import debounce from 'lodash.debounce';
import ToolbarCardParticipantItem from "./ToolbarCardParticipantItem";
import {EventBus} from "../../lib/event-bus";

export default {
    name: 'Toolbar',
    components: {
        'toolbar-button': ToolbarButton,
        'toolbar-card': ToolbarCard,
        'toolbar-card-list-item': ToolbarCardListItem,
        'toolbar-card-participant-item': ToolbarCardParticipantItem,
        'volume-slider': VolumeSlider,
    },
    props: {
        debug: {
            required: false,
            default: false,
        },
        fullscreen: {
            required: true,
            type: Boolean
        },
        full_viewport: {
            required: true,
            type: Boolean
        },
        sidepanel_is_expanded: {
            required: true,
            type: Boolean,
        },
        live_room: {
            required: true,
            type: Object,
        },
        livekit_room: {
            required: true,
            type: Object,
        },
        live_room_participant: {
            required: true,
            type: Object
        },
        is_connected: {
            default: false,
            type: Boolean,
        },
        publishers_length: {
            default: 0,
            type: Number,
        },
    },
    data() {
        return {
            accepted_screenshare_takeover: null,
            screen_options_is_open: false,
            show_participant_list: false,
            show_audio_devices_list: false,
            show_camera_devices_list: false,
            show_end_dialog: false,
            show_cant_play_audio_dialog: false,
            disable_click_outside: false,
            search_query: '',
            participants_has_changed: 0,
            active_audio_device: '',
            active_camera_device: '',
            audio_devices: [],
            camera_devices: [],
            width: 0,
            user,
            invited_participant_ids: [],
        }
    },
    watch: {
        is_connected(connected) {
            if (connected && this.livekit_room) {
                this.participants_has_changed++;
            }
        }
    },
    computed: {
        fullscreen_enabled() {
            return document.fullscreenEnabled;
        },
        local_participant_metadata() {
            return JSON.parse(this.livekit_room.localParticipant.metadata);
        },
        sorted_participants() {
            this.participants_has_changed; // We need this since vue isn't reactive on Map objects

            const sorted_participants = [];
            const participants_array = Array.from(this.livekit_room.participants.values());

            participants_array.forEach((participant) => {
               if(participant.permissions.canPublish) {
                   sorted_participants.unshift(participant);
               } else {
                   sorted_participants.push(participant);
               }
            });

            if(this.search_query) {
                return sorted_participants.filter(participant => {
                    const name = JSON.parse(participant.metadata).user.firstname + JSON.parse(participant.metadata).user.lastname;
                    const name_without_spaces = name.replace(/\s+/g, '');
                    return name_without_spaces.toLowerCase().includes(this.search_query.toLowerCase());
                });
            }

            return sorted_participants;
        },
    },
    methods: {
        handleToolbarCardItemClicked(participant) {
            if (this.invited_participant_ids.includes(Number(participant.identity))) {
                this.withdrawInvite(Number(participant.identity));
                const participantIndex = this.invited_participant_ids.findIndex((id) => id === Number(participant.identity));
                if(participantIndex !== -1) {
                    this.invited_participant_ids.splice(participantIndex, 1);
                }
            } else {
                this.toggleCoHost(participant)
            }
        },
        withdrawInvite(identity) {
            this.live_room.presence_channel.whisper('withdrewInvite', {
                participant_id: Number(identity)
            });
        },
        async showParticipantsList() {
            this.show_participant_list = !this.show_participant_list;
        },
        async showAudioDevicesList() {
            this.disable_click_outside = true;
            this.audio_devices = await Room.getLocalDevices('audioinput');
            this.show_audio_devices_list = !this.show_audio_devices_list;
            this.disable_click_outside = false;
        },
        async showPrompt() {
            this.show_end_dialog = !this.show_end_dialog;
        },
        async switchAudioDevice(device) {
            this.livekit_room.switchActiveDevice('audioinput', device.deviceId).then(() => {
                this.active_audio_device = device;
            });
            if (this.live_room.local_camera_is_muted) {
                await this.livekit_room.localParticipant.setMicrophoneEnabled(true);
            }
        },
        async showCameraDevicesList() {
            this.disable_click_outside = true;
            this.camera_devices = await Room.getLocalDevices('videoinput');
            this.show_camera_devices_list = !this.show_camera_devices_list;
            this.disable_click_outside = false;
        },
        async switchCameraDevice(device) {
            this.livekit_room.switchActiveDevice('videoinput', device.deviceId).then(() => {
                this.active_camera_device = device;
            });
            if (this.live_room.local_camera_is_muted) {
                await this.livekit_room.localParticipant.setCameraEnabled(true);
            }
        },
        // Middle button group handlers
        muteCamera() {
            this.show_camera_devices_list = false;
            this.livekit_room.localParticipant.setCameraEnabled(!this.livekit_room.localParticipant.isCameraEnabled);
        },
        requestScreenShareTakeover() {
            return new Promise((resolve, reject) => {
                this.live_room.presence_channel.whisper('requestScreenShareTakeover', {
                    current_screen_sharer_sid: this.live_room.screen_sharer_sid,
                    requester_sid: this.livekit_room.localParticipant.sid
                });

                this.live_room.presence_channel.listenForWhisper('screenShareTakeoverResponse', (event) => {
                    if (event.requester_sid === this.livekit_room.localParticipant.sid && event.accepted) {
                        this.accepted_screenshare_takeover = true;
                        return;
                    }

                    this.accepted_screenshare_takeover = false;
                });

                let index = 0;
                const interval = setInterval(() => {
                    index++;
                    if (index > 60) {
                        clearInterval(interval);
                        reject('Time exceeded');
                    }
                    if (this.accepted_screenshare_takeover) {
                        clearInterval(interval);
                        resolve(true);
                    }
                }, 1000);
            })
        },
        async muteScreen() {
            if (this.livekit_room.localParticipant.isScreenShareEnabled) {
                const current_tracks = this.livekit_room.localParticipant.getTracks();
                current_tracks.forEach(publication => {
                    if (publication.track.source === Track.Source.ScreenShare || publication.track.source === Track.Source.ScreenShareAudio) {
                        this.livekit_room.localParticipant.unpublishTrack(publication.track);
                    }
                })
            } else {
                const tracks = await this.livekit_room.localParticipant.createScreenTracks({
                    audio: true,
                    resolution: {
                        width: 1920,
                        height: 1080,
                        frameRate: 60,
                    },
                });
                if (this.live_room.screen_sharer_sid) {
                    this.accepted_screenshare_takeover = false;
                    this.requestScreenShareTakeover().then(async () => {
                        this.accepted_screenshare_takeover = null;
                        this.live_room.screen_sharer_sid = null;
                        for (let i = 0; i < tracks.length; i++) {
                            await this.livekit_room.localParticipant.publishTrack(tracks[i], {
                                source: tracks[i].source,
                            })
                        }
                    });
                } else {
                    for (let i = 0; i < tracks.length; i++) {
                        await this.livekit_room.localParticipant.publishTrack(tracks[i], {
                            source: tracks[i].source,
                        })
                    }
                }
            }
        },
        muteMicrophone() {
            this.show_audio_devices_list = false;
            this.livekit_room.localParticipant.setMicrophoneEnabled(!this.livekit_room.localParticipant.isMicrophoneEnabled);
        },
        muteScreenAudio() {
            const track = this.livekit_room.localParticipant.getTrack(Track.Source.ScreenShareAudio);
            track.isMuted ? track.unmute() : track.mute();
        },
        expandSidePanel() {
            this.$emit('expand-sidepanel');
            setTimeout(() => this.setElementWidth(), 50)
        },
        async chooseScreenTrack() {
            this.screen_options_is_open = true;
            try {
                const tracks = await this.livekit_room.localParticipant.createScreenTracks({audio: true});
                if (tracks.length > 0) {
                    // Check if there already is a screen share track and remove
                    const current_track_publications = this.livekit_room.localParticipant.getTracks();
                    current_track_publications.forEach(publication => {
                        if (publication.track.source === Track.Source.ScreenShare || publication.track.source === Track.Source.ScreenShareAudio) {
                            this.livekit_room.localParticipant.unpublishTrack(publication.track);
                        }
                    })

                    setTimeout(() => { // this solves a bug locally when running multiple instances of chrome with a live_room
                        tracks.forEach((track) => this.livekit_room.localParticipant.publishTrack(track, {
                            source: track.source,
                        }));
                    }, 1000)
                }
            } catch (e) {
                this.screen_options_is_open = false;
                if (this.debug) console.log(`RoomControls.vue - chooseScreenTrack() - ${e}`);
            }
            this.screen_options_is_open = false;
        },
        async goLive() {
            const participant_id = this.livekit_room.localParticipant.id;
            this.live_room_participant.access_token = await getParticipantToken(this.live_room_participant.access_token, participant_id);
            this.live_room.status = await updateRoomStatus("STARTED", this.live_room.id);
        },
        async cancelStaging() {
            this.live_room.status = await updateRoomStatus("ENDED", this.live_room.id);
        },
        async endLive() {
            this.live_room.status = await updateRoomStatus("ENDED", this.live_room.id);
            await this.$router.push({name: 'events.show', params: {event_slug: this.$route.params.event_slug}});
        },
        toggleFullscreen() {
            this.$emit('toggle-fullscreen');
        },
        async toggleCoHost(participant) {
            if (!this.live_room_participant.room_admin) {
                return;
            }

            if (participant.permissions.canPublish) {
                this.live_room.presence_channel.whisper('removeAsHost', {
                    participant_id: Number(participant.identity)
                });
            } else {
                this.invited_participant_ids.push(Number(participant.identity));
                this.participants_has_changed++;
                this.live_room.presence_channel.whisper('inviteAsHost', {
                    participant_id: Number(participant.identity),
                    inviter: {
                        id: this.live_room_participant.id,
                        name: `${this.local_participant_metadata.user.firstname} ${this.local_participant_metadata.user.lastname}`,
                        avatar: this.local_participant_metadata.user.picture_80,
                    }
                });
            }
        },
        setElementWidth() {
            if (this.$refs.toolbar) {
                this.width = this.$refs.toolbar.getBoundingClientRect().width;
            }
        },
        setupEventHandlers() {
            // List all camera/audio devices
            if (this.live_room_participant.can_publish) {
                this.audio_devices = Room.getLocalDevices('audioinput');
                this.camera_devices = Room.getLocalDevices('videoinput');
            }
            this.livekit_room.on(RoomEvent.MediaDevicesChanged, async () => {
                this.audio_devices = await Room.getLocalDevices('audioinput');
                this.camera_devices = await Room.getLocalDevices('videoinput')
            })
            // We do this because of the edge case where a browser opens a room for the first time, the devices can only be listed after user has granted permission to the browser.
            this.livekit_room.localParticipant.on(ParticipantEvent.LocalTrackPublished, async (publication) => {
                this.audio_devices = await Room.getLocalDevices('audioinput');
                this.camera_devices = await Room.getLocalDevices('videoinput');
                if (publication.source === 'camera') {
                    this.camera_devices.forEach((device) => {
                        if (device.label === publication.track.mediaStreamTrack.label) {
                            this.active_camera_device = device;
                        }
                    })
                }
                if (publication.source === 'microphone') {
                    this.audio_devices.forEach((device) => {
                        if (device.label === publication.track.mediaStreamTrack.label) {
                            this.active_audio_device = device;
                        }
                    })
                }
            });
            this.livekit_room.on(RoomEvent.ParticipantDisconnected, () => {
                this.participants_has_changed++;
            })
            this.livekit_room.on(RoomEvent.ParticipantConnected, () => {
                this.participants_has_changed++;
            })
            this.livekit_room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
                if (!this.livekit_room.canPlaybackAudio) {
                    this.show_cant_play_audio_dialog = true;
                    this.live_room.volume = '0';
                } else {
                    this.show_cant_play_audio_dialog = false;
                    this.live_room.volume = '50';
                }
            });
        },

        playAudio() {
            this.livekit_room.startAudio();
        },
    },
    created() {
        window.addEventListener("resize", debounce(this.setElementWidth, 100));
    },
    destroyed() {
        EventBus.$off('removeInvitedId');
        window.removeEventListener("resize", debounce(this.setElementWidth, 100));
        EventBus.$off('removeInvitedId');
    },
    async mounted() {
        this.setElementWidth();
        this.setupEventHandlers();
        EventBus.$on('removeInvitedId', (invited_id) => {
            const participantIndex = this.invited_participant_ids.findIndex((id) => id === Number(invited_id));
            if(participantIndex !== -1) {
                this.invited_participant_ids.splice(participantIndex, 1);
                this.participants_has_changed++;
            }
        })
    }
};
</script>

<style scoped>
.divider-color {
    border-color: #f0f0f0;
}

.dropdown-menu-item >>> p {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.position-full-viewport {
    bottom: 2.286rem;
}

.position-in-event {
    bottom: 1.143rem;
}

.no-gap {
    gap: 0 !important;
}

.toolbar {
    background: rgba(5, 5, 5, 0.4);
    backdrop-filter: blur(10px);
}

.clickable-icon-override:hover {
    background: rgba(255, 255, 255, 0.1);
}

.clickable-icon-override:active {
    background: rgba(255, 255, 255, 0.2);
}

.general-button-override {
    font-size: 0.857rem;
    padding-left: 1.143rem;
    padding-right: 1.143rem;
    border-radius: 9999px;
    line-height: 21px;
    white-space: nowrap;
    color: white;
}

.transparent {
    background-color: transparent;
    color: #37474F; /* font primary */
    border: none;
    box-shadow: none;
}

.transparent:hover {
    background-color: #F0F0F0;
}

.bg-color-red {
    background: #FF5050;
}

.bg-color-blue {
    background: #2196F3;
}

@media (max-width: 768px) {
    .position-left-card {
        left: 0;
    }
}

.position-leave-card {
    position: absolute;
    right: 50%;
    transform: translateX(50%);
}

.position-participants-card {
    position: absolute;
    left: 25%;
}

.participant-card {
    min-width: 18rem;
}

.participants-search-icon {
    position: absolute;
    right: 20px;
    bottom: 50%;
    transform: translate(0, 50%);
    font-weight: 100;
    color: #696969;
    font-size: 15px;
}

.participants-search-input {
    border: none;
    -webkit-appearance: none;
    -ms-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background: #f2f2f2;
    font-size: 14px;
    padding: 0.714rem 0.714rem 0.714rem 1.143rem;
    border-radius: 4px;
    margin-bottom: 0 !important;
}

.participants-search-input:focus {
    color: #37474F !important;
}

.participants-search-input::-webkit-input-placeholder { /* Chrome/Opera/Safari */
    color: #C7C7C7;
}

.participants-search-input::-moz-placeholder { /* Firefox 19+ */
    color: #C7C7C7;
}

.participants-search-input:-ms-input-placeholder { /* IE 10+ */
    color: #C7C7C7;
}

.participants-search-input:-moz-placeholder { /* Firefox 18- */
    color: #C7C7C7;
}
</style>
